import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AlertTriangle, MailCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { api, errorMessage } from "@/lib/api";
import { AuthShell, FormError } from "./AuthShell";

export default function CheckEmail() {
  const { user, reload, logout } = useAuth();
  const navigate = useNavigate();
  const { state: navState } = useLocation();
  const state = navState || JSON.parse(sessionStorage.getItem("evr_signup_email") || "null");
  const [sent, setSent] = useState(state?.emailSent ?? null);
  const [failMsg, setFailMsg] = useState(state?.emailError || "");
  const [status, setStatus] = useState(state?.emailStatus || null);
  const [error, setError] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const resend = async () => {
    setError(""); setNote(""); setBusy(true);
    try {
      const { data } = await api.post("/auth/resend-verification");
      if (data.already_verified) return navigate("/app", { replace: true });
      setSent(data.email_sent); setStatus(data.email_status); setFailMsg(data.email_error || "");
      if (data.email_sent) setNote("Our email provider accepted a new link. Check your inbox and spam folder.");
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const checkAgain = async () => {
    const u = await reload();
    if (u && u.email_verified) navigate("/app", { replace: true });
    else setNote("Your email isn't confirmed yet. Open the link in the email we sent.");
  };

  return (
    <AuthShell eyebrow="One more step" title="Confirm your email"
      subtitle={<>We need to confirm <span className="font-medium text-navy" data-testid="check-email-address">{user?.email}</span> before you can use your workspace.</>}>
      <div className="space-y-5" data-testid="check-email-panel">
        {sent === false ? (
          <div data-testid={status === "rate_limited" ? "email-rate-limited" : "email-delivery-unavailable"} className="flex gap-3 rounded-md border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900">
            <AlertTriangle className="h-5 w-5 shrink-0" />
            <p>{failMsg || "Email delivery is unavailable right now, so we couldn't send your confirmation link. Please try again in a few minutes."}</p>
          </div>
        ) : (
          <div data-testid="email-sent-notice" className="flex gap-3 rounded-md border border-gold/40 bg-gold-light/50 p-4 text-sm text-navy">
            <MailCheck className="h-5 w-5 shrink-0 text-gold-dark" />
            <p>{sent ? "Our email provider accepted your confirmation email. That doesn't confirm it reached your inbox. It can take a few minutes, so check spam too. The link works for 24 hours." : "Open the confirmation link we emailed you. It works for 24 hours. If it hasn't arrived, send a new link."}</p>
          </div>
        )}
        {note && <p data-testid="check-email-note" className="text-sm text-slate-600">{note}</p>}
        <FormError message={error} testId="check-email-error" />
        <div className="flex flex-col gap-3 sm:flex-row">
          <Button onClick={checkAgain} data-testid="check-email-continue-button" className="h-11 flex-1 bg-navy hover:bg-navy-2">I've confirmed it</Button>
          <Button onClick={resend} disabled={busy} variant="outline" data-testid="check-email-resend-button" className="h-11 flex-1">
            {busy ? "Sending..." : "Send a new link"}
          </Button>
        </div>
        <button onClick={logout} data-testid="check-email-signout-button" className="text-sm text-slate-500 hover:text-navy underline underline-offset-4">Sign out</button>
      </div>
    </AuthShell>
  );
}
